import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Upload, Trash2, Download, Loader2, AlertTriangle, BadgeCheck } from "lucide-react";
import { DOCS, MEPA_DOC, DECLARATION, itDate, downloadFile } from "@/lib/pa";
import { DateField, todayRome } from "@/components/DateField";

function DocRow({ doc, rec, declared, withNote, onDone, userId }) {
  const ref = useRef(null);
  const [validUntil, setValidUntil] = useState("");
  const [note, setNote] = useState(rec?.note || "");
  const [busy, setBusy] = useState(false);
  const expired = rec?.validUntil && rec.validUntil < todayRome();

  const pick = () => {
    if (!declared) { toast.error("Accetta prima la dichiarazione di responsabilità"); return; }
    if (doc.expiry && !validUntil) { toast.error("Indica la data di scadenza del documento"); return; }
    ref.current?.click();
  };
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("declaration", "true");
    if (validUntil) fd.append("validUntil", validUntil);
    if (withNote && note.trim()) fd.append("note", note.trim());
    setBusy(true);
    try { await api.post(`/me/compliance/${doc.key}`, fd); toast.success("Documento caricato"); setValidUntil(""); onDone(); }
    catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Caricamento non riuscito"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const remove = async () => { await api.delete(`/me/compliance/${doc.key}`); toast.success("Documento rimosso"); onDone(); };

  return (
    <div className="border border-slate-200 rounded-xl p-4 space-y-3" data-testid={`compliance-row-${doc.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold text-sm text-navy">{doc.label}</div>
          {rec ? <div className={`text-xs mt-1 ${expired ? "text-red-600" : "text-emerald-700"}`} data-testid={`compliance-status-${doc.key}`}>
            {expired ? "Scaduto" : "Caricato"} il {itDate(rec.uploadedAt)}{rec.validUntil && ` · valido fino al ${itDate(rec.validUntil)}`} · {rec.filename}</div>
            : <div className="text-xs mt-1 text-slate-500" data-testid={`compliance-status-${doc.key}`}>Non caricato</div>}
        </div>
        {rec && <div className="flex gap-2">
          <button type="button" data-testid={`compliance-view-${doc.key}`} onClick={() => downloadFile(`/compliance/${userId}/${doc.key}`, rec.filename)} className="p-2 rounded-full border border-slate-300 hover:border-gold" aria-label="Scarica"><Download className="w-4 h-4"/></button>
          <button type="button" data-testid={`compliance-remove-${doc.key}`} onClick={remove} className="p-2 rounded-full border border-slate-300 hover:border-red-400 text-red-600" aria-label="Rimuovi"><Trash2 className="w-4 h-4"/></button>
        </div>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {doc.expiry && <label className="text-xs text-slate-600 flex items-center gap-2">Scadenza
          <DateField testId={`compliance-expiry-${doc.key}`} value={validUntil} min={todayRome()} onChange={setValidUntil} className="border border-slate-300 rounded-lg px-2 py-1 text-xs"/></label>}
        {withNote && <input data-testid={`compliance-note-${doc.key}`} value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Bandi / categorie Consip di abilitazione (es. Servizi - Organizzazione eventi)" className="flex-1 min-w-[220px] border border-slate-300 rounded-lg px-3 py-1.5 text-xs"/>}
        <input ref={ref} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" data-testid={`compliance-input-${doc.key}`} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}/>
        <button type="button" data-testid={`compliance-upload-${doc.key}`} disabled={busy} onClick={pick} className="btn-pill-outline text-xs">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Upload className="w-3.5 h-3.5"/>}{rec ? "Sostituisci" : "Carica"} (PDF, JPG, PNG · max 10 MB)</button>
      </div>
    </div>
  );
}

export function ComplianceCard() {
  const { user, refresh } = useAuth();
  const [declared, setDeclared] = useState(false);
  const comp = user?.compliance || {};
  const eligible = /^\d{11}$/.test((user?.vatNumber || "").trim());

  return (
    <div className="mt-8 card-premium p-6 space-y-5" data-testid="compliance-card">
      <div>
        <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-5 h-5 text-gold"/><h3 className="font-display text-xl font-bold">Kit Trasparenza PA e abilitazione MePA</h3></div>
        <p className="text-sm text-slate-600">I documenti sono visibili e scaricabili solo da Enti Pubblici e Committenti registrati, con un solo click dalla tua scheda.</p>
      </div>
      {!eligible ? (
        <p className="text-sm text-amber-700 flex items-start gap-2" data-testid="compliance-no-vat"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>Disponibile per gli operatori con Partita IVA: aggiungi la tua P.IVA (11 cifre) nei dati di fatturazione per attivare il Kit.</p>
      ) : (<>
        <label className="flex items-start gap-2 text-sm bg-gold/10 border border-gold/40 rounded-xl p-4 cursor-pointer" data-testid="compliance-declaration">
          <input type="checkbox" checked={declared} onChange={(e) => setDeclared(e.target.checked)} className="mt-1 accent-[#D5A34C]" data-testid="compliance-declaration-check"/>
          <span>{DECLARATION}</span>
        </label>
        <div className="space-y-3">{DOCS.map((d) => <DocRow key={d.key} doc={d} rec={comp[d.key]} declared={declared} onDone={refresh} userId={user.id}/>)}</div>
        <div>
          <div className="overline text-gold mb-2 flex items-center gap-2"><BadgeCheck className="w-4 h-4"/>Badge "Abilitato MePA"</div>
          <p className="text-xs text-slate-600 mb-3">Carica la prova di abilitazione ai bandi Consip: il badge compare subito nel tuo profilo pubblico.</p>
          <DocRow doc={MEPA_DOC} rec={comp.mepa} declared={declared} withNote onDone={refresh} userId={user.id}/>
        </div>
      </>)}
    </div>
  );
}
